import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Badge,
  Divider
} from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const FinancialServicesGuide: React.FC = () => {
  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/financial-services-guide"
        fallbackTitle="Financial Services Guide"
        fallbackDescription="Understanding our financial services and advice"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg="teal.700" color="white" py={14}>
        <Container maxW="container.xl">
          <VStack spacing={4} align="start">
            <Badge colorScheme="teal" fontSize="xs" px={3} py={1} borderRadius="full" bg="teal.500">
              ASIC Compliance Document
            </Badge>
            <Heading size="2xl" fontWeight="bold">
              Financial Services Guide (FSG)
            </Heading>
            <Text fontSize="lg" color="teal.100" maxW="3xl">
              This guide helps you understand the financial services we provide, how we are paid,
              and your rights as a client. This FSG is issued in accordance with the Corporations Act 2001.
            </Text>
            <Text fontSize="sm" color="teal.200" mt={2}>
              Version 1.0 | Last Updated: November 2025
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.lg" py={16}>
        <VStack spacing={10} align="stretch">

          {/* About This Guide */}
          <Box bg="white" p={8} borderRadius="lg" shadow="sm">
            <Heading size="lg" mb={4} color="teal.800">About This Financial Services Guide</Heading>
            <Text color="gray.700" lineHeight="tall" mb={4}>
              <strong>[PLACEHOLDER CONTENT]</strong> This Financial Services Guide (FSG) is designed to assist you in
              deciding whether to use the financial services we offer. It contains information about:
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={6}>
              <Box p={4} bg="teal.50" borderRadius="md" borderLeft="3px solid" borderColor="teal.500">
                <Text fontSize="sm" fontWeight="semibold" color="teal.800" mb={2}>Who We Are</Text>
                <Text fontSize="sm" color="gray.700">Our company details and licensing information</Text>
              </Box>
              <Box p={4} bg="teal.50" borderRadius="md" borderLeft="3px solid" borderColor="teal.500">
                <Text fontSize="sm" fontWeight="semibold" color="teal.800" mb={2}>Services Provided</Text>
                <Text fontSize="sm" color="gray.700">The financial services we are authorized to provide</Text>
              </Box>
              <Box p={4} bg="teal.50" borderRadius="md" borderLeft="3px solid" borderColor="teal.500">
                <Text fontSize="sm" fontWeight="semibold" color="teal.800" mb={2}>How We Are Paid</Text>
                <Text fontSize="sm" color="gray.700">Our remuneration structure and fees</Text>
              </Box>
              <Box p={4} bg="teal.50" borderRadius="md" borderLeft="3px solid" borderColor="teal.500">
                <Text fontSize="sm" fontWeight="semibold" color="teal.800" mb={2}>Your Rights</Text>
                <Text fontSize="sm" color="gray.700">Complaint procedures and dispute resolution</Text>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider />

          {/* Company Information */}
          <Box bg="white" p={8} borderRadius="lg" shadow="sm">
            <Heading size="lg" mb={6} color="teal.800">1. About One Group</Heading>
            <VStack align="stretch" spacing={6}>
              <Box>
                <Heading size="sm" mb={3} color="gray.800">Company Details</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.600">Legal Name</Text>
                    <Text fontSize="md" color="gray.700">[PLACEHOLDER] One Group Pty Ltd</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.600">ABN</Text>
                    <Text fontSize="md" color="gray.700">XX XXX XXX XXX</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.600">AFSL Number</Text>
                    <Text fontSize="md" color="gray.700">[PLACEHOLDER] XXXXXX</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.600">Credit License</Text>
                    <Text fontSize="md" color="gray.700">[PLACEHOLDER] XXXXXX</Text>
                  </Box>
                </SimpleGrid>
              </Box>

              <Box>
                <Heading size="sm" mb={3} color="gray.800">Contact Information</Heading>
                <VStack align="start" spacing={2} fontSize="sm" color="gray.700">
                  <Text><strong>Address:</strong> [Placeholder Address], Melbourne VIC 3000</Text>
                  <Text><strong>Phone:</strong> 1300 XXX XXX</Text>
                  <Text><strong>Email:</strong> info@onegroup.com.au</Text>
                  <Text><strong>Website:</strong> www.onegroup.com.au</Text>
                </VStack>
              </Box>

              <Box bg="teal.50" p={4} borderRadius="md">
                <Text fontSize="sm" color="gray.700">
                  <strong>[PLACEHOLDER]</strong> One Group Pty Ltd is a corporate authorized representative of
                  [Placeholder Licensee Name] (AFSL XXXXXX). We are authorized to provide financial services
                  including financial product advice and dealing services in relation to business management,
                  financial planning, and related services.
                </Text>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Services Provided */}
          <Box bg="white" p={8} borderRadius="lg" shadow="sm">
            <Heading size="lg" mb={6} color="teal.800">2. Services We Provide</Heading>
            <Text color="gray.700" lineHeight="tall" mb={6}>
              <strong>[PLACEHOLDER]</strong> We are authorized to provide the following financial services
              under our Australian Financial Services License:
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={5} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
                <Heading size="sm" mb={2} color="teal.700">Business Advisory</Heading>
                <Text fontSize="sm" color="gray.700">
                  Strategic planning, business structuring, and growth consultation services for small to
                  medium enterprises.
                </Text>
              </Box>
              <Box p={5} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
                <Heading size="sm" mb={2} color="teal.700">Financial Planning</Heading>
                <Text fontSize="sm" color="gray.700">
                  General advice on financial products, budgeting, cash flow management, and business
                  financial strategies.
                </Text>
              </Box>
              <Box p={5} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
                <Heading size="sm" mb={2} color="teal.700">Property Services</Heading>
                <Text fontSize="sm" color="gray.700">
                  Property portfolio management, investment property advice, and real estate financial
                  planning services.
                </Text>
              </Box>
              <Box p={5} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
                <Heading size="sm" mb={2} color="teal.700">Technology Solutions</Heading>
                <Text fontSize="sm" color="gray.700">
                  Business management platform services, CRM systems, and digital transformation consulting.
                </Text>
              </Box>
            </SimpleGrid>
            <Box mt={6} p={4} bg="yellow.50" borderRadius="md" borderLeft="3px solid" borderColor="yellow.500">
              <Text fontSize="sm" color="gray.700">
                <strong>Important:</strong> The advice we provide is general in nature and does not take into
                account your personal objectives, financial situation, or needs. Before acting on any advice,
                you should consider whether it is appropriate for your circumstances.
              </Text>
            </Box>
          </Box>

          <Divider />

          {/* Remuneration */}
          <Box bg="white" p={8} borderRadius="lg" shadow="sm">
            <Heading size="lg" mb={6} color="teal.800">3. How We Are Paid</Heading>
            <VStack align="stretch" spacing={6}>
              <Text color="gray.700" lineHeight="tall">
                <strong>[PLACEHOLDER]</strong> We receive remuneration for our services in the following ways:
              </Text>

              <Box>
                <Heading size="sm" mb={3} color="gray.800">Subscription Fees</Heading>
                <Text fontSize="sm" color="gray.700" mb={3}>
                  Our primary source of revenue is monthly or annual subscription fees for access to our
                  business management platform. Fees vary by subscription tier:
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} fontSize="sm">
                  <Box p={3} bg="gray.50" borderRadius="md">
                    <Text fontWeight="semibold">Foundation Tier</Text>
                    <Text color="teal.600">$97 - $297/month</Text>
                  </Box>
                  <Box p={3} bg="gray.50" borderRadius="md">
                    <Text fontWeight="semibold">Professional Tier</Text>
                    <Text color="teal.600">$297 - $897/month</Text>
                  </Box>
                  <Box p={3} bg="gray.50" borderRadius="md">
                    <Text fontWeight="semibold">Enterprise Tier</Text>
                    <Text color="teal.600">$897 - $5,997/month</Text>
                  </Box>
                </SimpleGrid>
              </Box>

              <Box>
                <Heading size="sm" mb={3} color="gray.800">Advisory Fees</Heading>
                <Text fontSize="sm" color="gray.700">
                  For specialized financial advisory services, we may charge:
                </Text>
                <VStack align="start" spacing={2} mt={2} fontSize="sm" color="gray.700" pl={4}>
                  <Text>• Hourly rate: $150 - $350 per hour (depending on advisor level)</Text>
                  <Text>• Fixed project fees: Quoted based on scope of work</Text>
                  <Text>• Retainer arrangements: Available for ongoing advisory services</Text>
                </VStack>
              </Box>

              <Box>
                <Heading size="sm" mb={3} color="gray.800">Third-Party Commissions</Heading>
                <Text fontSize="sm" color="gray.700">
                  We may receive commissions from third-party providers when we refer clients to their services.
                  These may include insurance products, financial products, or business services. Any commissions
                  received will be disclosed to you before you proceed with the service.
                </Text>
              </Box>

              <Box bg="teal.50" p={4} borderRadius="md">
                <Text fontSize="sm" color="gray.700">
                  <strong>Fee Disclosure:</strong> All fees and charges will be clearly disclosed to you before
                  you engage our services. You have the right to request additional information about our
                  remuneration at any time.
                </Text>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Associations and Relationships */}
          <Box bg="white" p={8} borderRadius="lg" shadow="sm">
            <Heading size="lg" mb={6} color="teal.800">4. Professional Associations and Relationships</Heading>
            <Text color="gray.700" lineHeight="tall" mb={6}>
              <strong>[PLACEHOLDER]</strong> We maintain the following professional associations and relationships:
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={4} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={2}>Industry Memberships</Text>
                <VStack align="start" spacing={1} fontSize="sm" color="gray.700">
                  <Text>• Financial Planning Association (FPA)</Text>
                  <Text>• Australian Financial Complaints Authority (AFCA)</Text>
                  <Text>• Real Estate Institute of Australia</Text>
                </VStack>
              </Box>
              <Box p={4} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={2}>Strategic Partnerships</Text>
                <VStack align="start" spacing={1} fontSize="sm" color="gray.700">
                  <Text>• [Placeholder Partner 1]</Text>
                  <Text>• [Placeholder Partner 2]</Text>
                  <Text>• [Placeholder Partner 3]</Text>
                </VStack>
              </Box>
            </SimpleGrid>
            <Box mt={4} p={4} bg="blue.50" borderRadius="md">
              <Text fontSize="sm" color="gray.700">
                We maintain independence in our advice and disclose any potential conflicts of interest that
                may arise from our professional relationships.
              </Text>
            </Box>
          </Box>

          <Divider />

          {/* Complaints Process */}
          <Box bg="white" p={8} borderRadius="lg" shadow="sm">
            <Heading size="lg" mb={6} color="teal.800">5. Complaints and Dispute Resolution</Heading>
            <Text color="gray.700" lineHeight="tall" mb={6}>
              <strong>[PLACEHOLDER]</strong> We are committed to providing quality services. If you have a
              complaint about our services, please follow this process:
            </Text>
            <VStack align="stretch" spacing={4}>
              <Box p={4} bg="teal.50" borderRadius="md" borderLeft="4px solid" borderColor="teal.500">
                <Text fontSize="sm" fontWeight="semibold" color="teal.800" mb={2}>Step 1: Contact Us Directly</Text>
                <Text fontSize="sm" color="gray.700">
                  Email complaints@onegroup.com.au or call 1300 XXX XXX. We will acknowledge your complaint
                  within 24 hours and aim to resolve it within 30 days.
                </Text>
              </Box>
              <Box p={4} bg="teal.50" borderRadius="md" borderLeft="4px solid" borderColor="teal.500">
                <Text fontSize="sm" fontWeight="semibold" color="teal.800" mb={2}>Step 2: Internal Review</Text>
                <Text fontSize="sm" color="gray.700">
                  If not satisfied with the initial response, request an internal review by our Compliance
                  Manager. We will conduct a thorough investigation and provide a detailed response.
                </Text>
              </Box>
              <Box p={4} bg="teal.50" borderRadius="md" borderLeft="4px solid" borderColor="teal.500">
                <Text fontSize="sm" fontWeight="semibold" color="teal.800" mb={2}>Step 3: External Dispute Resolution</Text>
                <Text fontSize="sm" color="gray.700">
                  If still not resolved, you can refer the matter to the Australian Financial Complaints
                  Authority (AFCA). This service is free to consumers.
                </Text>
                <VStack align="start" spacing={1} mt={3} fontSize="sm" color="gray.700">
                  <Text><strong>AFCA:</strong> Australian Financial Complaints Authority</Text>
                  <Text><strong>Website:</strong> www.afca.org.au</Text>
                  <Text><strong>Phone:</strong> 1800 931 678</Text>
                  <Text><strong>Email:</strong> info@afca.org.au</Text>
                  <Text><strong>Mail:</strong> GPO Box 3, Melbourne VIC 3001</Text>
                </VStack>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Privacy Statement */}
          <Box bg="white" p={8} borderRadius="lg" shadow="sm">
            <Heading size="lg" mb={6} color="teal.800">6. Privacy and Confidentiality</Heading>
            <Text color="gray.700" lineHeight="tall" mb={4}>
              <strong>[PLACEHOLDER]</strong> We are committed to protecting your privacy in accordance with the
              Privacy Act 1988 and the Australian Privacy Principles. We collect personal information to:
            </Text>
            <VStack align="start" spacing={2} fontSize="sm" color="gray.700" pl={4} mb={4}>
              <Text>• Provide you with our financial services</Text>
              <Text>• Comply with legal and regulatory obligations</Text>
              <Text>• Improve our services and customer experience</Text>
              <Text>• Communicate with you about our services</Text>
            </VStack>
            <Text fontSize="sm" color="gray.700">
              For detailed information about how we handle your personal information, please refer to our
              Privacy Policy available on our website or by contacting us.
            </Text>
          </Box>

          {/* Footer Note */}
          <Box bg="teal.50" p={6} borderRadius="md" borderLeft="4px solid" borderColor="teal.600">
            <Heading size="sm" mb={3} color="teal.900">Important Information</Heading>
            <Text fontSize="sm" color="gray.700">
              <strong>[PLACEHOLDER]</strong> This Financial Services Guide is Version 1.0, dated November 2025.
              If we make any material changes to this FSG, we will issue you with an updated version or a
              supplementary document. You can request a copy of this FSG at any time by contacting us.
            </Text>
            <Text fontSize="sm" color="gray.700" mt={3}>
              For questions about this FSG or our services, please contact our Client Services team at
              info@onegroup.com.au or 1300 XXX XXX.
            </Text>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinancialServicesGuide;
